import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, homePathForRole } from '@/lib/auth';
import { ApiException, authApi } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { GoogleButton } from '@/components/GoogleButton';

export default function LoginPage() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    async function handleSubmit(e: FormEvent) {
        e.preventDefault();
        setSubmitting(true);
        setError(null);
        try {
            await login(email, password);
            // Después de login, leer el user actual y redirigir según role.
            const user = await authApi.getCurrentUser();
            navigate(homePathForRole(user.role), { replace: true });
        } catch (err) {
            if (err instanceof ApiException && err.status === 401) {
                setError('Correo o contraseña incorrectos.');
            } else {
                setError((err as Error).message);
            }
        } finally {
            setSubmitting(false);
        }
    }

    async function handleGoogle(credential: string) {
        setSubmitting(true);
        setError(null);
        try {
            const r = await authApi.google(credential);
            navigate(homePathForRole(r.user.role), { replace: true });
        } catch (err) {
            if (err instanceof ApiException && err.status === 403) {
                setError('Cuenta no registrada — pídele invitación a tu administrador.');
            } else {
                setError((err as Error).message);
            }
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <div className="min-h-screen flex flex-col lg:flex-row">
            {/* Left panel — brand */}
            <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-brand-blue-dark via-brand-blue to-brand-blue-light p-16 flex-col justify-between text-white">
                <div className="space-y-10">
                    <div>
                        <h1 className="text-6xl font-extrabold tracking-tight">ConexEd</h1>
                        <p className="text-lg text-white/80 mt-3 font-medium">Gestión Integral de Prácticas Profesionales</p>
                        <p className="text-xs text-white/50 mt-2 tracking-wide uppercase">Por Conexium</p>
                    </div>
                    <div className="w-10 h-px bg-white/20" />
                    <ul className="space-y-5">
                        {[
                            ['Control de asistencia y bitácora', 'Registro diario de horas, actividades y avance'],
                            ['Evaluaciones con rúbricas', 'Plantillas estandarizadas con trazabilidad completa'],
                            ['Todos los roles en una plataforma', 'Profesores, tutores, estudiantes y administradores'],
                            ['Portafolio digital', 'Cada estudiante documenta y reflexiona su experiencia'],
                        ].map(([title, desc]) => (
                            <li key={title} className="flex gap-4">
                                <span className="w-1.5 h-1.5 mt-2 rounded-full bg-white/80 flex-shrink-0" />
                                <div>
                                    <p className="font-medium">{title}</p>
                                    <p className="text-sm text-white/70 mt-0.5">{desc}</p>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
                <div className="text-xs text-white/40">ConexEd v2.0</div>
            </div>

            {/* Right panel — form */}
            <div className="flex-1 flex items-center justify-center p-6 lg:p-12 bg-white dark:bg-gray-900">
                <div className="w-full max-w-md space-y-6">
                    <div className="lg:hidden text-center mb-8">
                        <h1 className="text-3xl font-extrabold text-brand-blue">ConexEd</h1>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Gestión de Prácticas · Por Conexium</p>
                    </div>

                    <div>
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Iniciar sesión</h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                            Bienvenido/a de nuevo a ConexEd.
                        </p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <Input
                            label="Email"
                            type="email"
                            name="email"
                            autoComplete="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                        />
                        <Input
                            label="Contraseña"
                            type="password"
                            name="password"
                            autoComplete="current-password"
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                        />

                        {error && (
                            <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 px-3 py-2 rounded-lg">
                                {error}
                            </div>
                        )}

                        <Button type="submit" loading={submitting} className="w-full">
                            Ingresar
                        </Button>

                        <div className="text-right">
                            <Link to="/forgot-password" className="text-sm text-brand-blue hover:underline">
                                ¿Olvidaste tu contraseña?
                            </Link>
                        </div>
                    </form>

                    <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-gray-200 dark:border-gray-700" />
                        </div>
                        <div className="relative flex justify-center text-xs">
                            <span className="px-2 bg-white dark:bg-gray-900 text-gray-500">o continúa con</span>
                        </div>
                    </div>
                    <GoogleButton onCredential={handleGoogle} text="signin_with" />

                    <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                        <p className="text-center text-xs text-gray-500 dark:text-gray-400 mb-2">¿Primera vez en ConexEd?</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <Link
                                to="/registro-individual"
                                className="text-center text-sm py-2 px-3 rounded-lg border border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition"
                            >
                                Crear cuenta individual
                            </Link>
                            <span className="text-center text-sm py-2 px-3 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400">
                                ¿Tienes invitación? Abre el link del email
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
